import OfferDetailPopup from "@/components/shop/OfferDetailPopup.vue";
import type { Offer } from "@/game/config/gameCurrencyOffers";
import type { ResourceType } from "@/game/entities/config/resources";
import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useGameTicker from "@/hooks/useGameTicker";
import useSocketService from "@/hooks/useSocketService";
import Communicator from "@/libs/AppCommunicatior";
import { EmitStores, OfferAnalyticsType } from "@/libs/SocketService";
import router from "@/router";
import { CalculationType, OfferProvider } from "@/services/OfferProvider";
import type { OfferAnalytics } from "@/store/usePopupStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import useSettingsStore from "@/store/useSettingsStore";
import useShopStore from "@/store/useShopStore";
import useUserStore from "@/store/useUserStore";
import type { ShopItem } from "@/types/shop";
import { OfferStatus } from "@/types/shop";
import { OfferType, RouteNames, SkinTypes, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { markRaw, toRaw } from "vue";

class PaymentProviderClass {
	// Lazy store accessors
	private _shopStore?: ReturnType<typeof useShopStore>;
	private get shopStore(): ReturnType<typeof useShopStore> {
		return (this._shopStore ??= useShopStore());
	}

	private _userStore?: ReturnType<typeof useUserStore>;
	private get userStore(): ReturnType<typeof useUserStore> {
		return (this._userStore ??= useUserStore());
	}

	private _resourcesStore?: ReturnType<typeof useResourcesStore>;
	private get resourcesStore(): ReturnType<typeof useResourcesStore> {
		return (this._resourcesStore ??= useResourcesStore());
	}

	private _popupStore?: ReturnType<typeof usePopupStore>;
	private get popupStore(): ReturnType<typeof usePopupStore> {
		return (this._popupStore ??= usePopupStore());
	}

	private _settingsStore?: ReturnType<typeof useSettingsStore>;
	private get settingsStore(): ReturnType<typeof useSettingsStore> {
		return (this._settingsStore ??= useSettingsStore());
	}

	// Helpers
	private _ticker?: ReturnType<typeof useGameTicker>;
	private get ticker(): ReturnType<typeof useGameTicker> {
		return (this._ticker ??= useGameTicker());
	}

	private _socket?: ReturnType<typeof useSocketService>;
	private get socket(): ReturnType<typeof useSocketService> {
		return (this._socket ??= useSocketService());
	}

	private get emitter(): ReturnType<typeof useEmitter> {
		return useEmitter();
	}

	// Shared logic to finalize a successful in-game purchase.
	private async finalizeInGamePurchase(offer: Offer): Promise<void> {
		const status = await this.handleResourcesUpdate(offer);

		if (status === OfferStatus.SUCCESS) {
			if (offer.type === OfferType.GEM_FOR_COINS) {
				this.userStore.setInGameGemPurchaseForCoinsOfferId(offer.id);
			}

			// Record timestamp for cooldowns
			this.userStore.setMarketplaceTimestamps(
				`${offer.type}_${offer.id}`,
				Date.now(),
			);

			offer.callback?.();

			if (offer.type !== OfferType.GEM_FOR_COINS) {
				this.emitter.emit(EmitterEvents.ShopItemPurchased, {
					offerId: offer.id,
					offerType: offer.type,
				});
			}

			await this.handleInAppPayment();

			// Recalculate UI offers based on new state
			OfferProvider.calculate([
				CalculationType.RESOURCES_FOR_GEMS_OFFERS,
			]);
		}
	}

	public buyPackage = (storeId: string, analytics?: OfferAnalytics): void => {
		const activeItems = toRaw(this.shopStore.activeItems);
		if (!activeItems) return;

		this.emitGenericClick();

		const offer = activeItems.find(
			(pack: ShopItem): boolean => pack?.store_id === storeId,
		);

		this.socket.emitOfferAnalyticsSave({
			type: OfferAnalyticsType.OFFER_PAY_REQUEST,
			clickSource: analytics?.clickSource ?? "UNKNOWN",
			storeId: storeId ?? "UNKNOWN",
			offerName: offer?.name ?? "UNKNOWN",
		});

		this.ticker.stop();
		Communicator.getInstance().buyProduct(storeId);
		this.closePopup();
	};

	public checkResources = async (offer: Offer): Promise<OfferStatus> => {
		const entries = Object.entries(offer.price) as Array<
			[ResourceType, number]
		>;
		for (const [key, value] of entries) {
			if (this.resourcesStore.getCurrencyValue(key) < value) {
				return OfferStatus.NOT_ENOUGH_RESOURCES;
			}
		}
		return OfferStatus.HAS_ENOUGH_RESOURCES;
	};

	public handleInAppPayment = async (): Promise<void> => {
		try {
			await this.socket.emitSaveRequest({
				stores: [EmitStores.RESOURCES, EmitStores.USER],
			});
			this.socket.emitLoadRequest({
				stores: [EmitStores.USER],
			});
		} finally {
			this.resumeGame();
		}
	};

	public handleResourcesUpdate = async (
		offer: Offer,
	): Promise<OfferStatus> => {
		const status = await this.checkResources(offer);
		if (status !== OfferStatus.HAS_ENOUGH_RESOURCES) return status;

		// Process costs
		Object.entries(offer.price).forEach(
			([key, value]: [string, number]): void => {
				this.resourcesStore.decrease(
					key as ResourceType,
					value as number,
					`shop - ${offer.type}`,
				);
			},
		);

		// Process rewards
		if (offer.value) {
			Object.entries(offer.value).forEach(
				([key, value]: [string, number | (() => number)]): void => {
					const resolvedValue =
						typeof value === "function" ? value() : value;

					this.resourcesStore.increase(
						key as ResourceType,
						resolvedValue,
						"shop",
					);
				},
			);
		}

		return OfferStatus.SUCCESS;
	};

	public handleMissingResource = (key: string): void => {
		this.resumeGame();
		this.popupStore.showPopup({
			id: PopupDictionary.NOT_ENOUGH_RESOURCES,
			type: PopupType.INFO,
			heading: translate("ui.shop"),
			body: translate(`ui.not_enough_${key}`),
			skin: SkinTypes.LIGHT,
			actions: [
				{
					label: translate(`button.buy_${key}`),
					callback: (): void => {
						this.popupStore.hidePopup();
						router.push({
							name: RouteNames.SHOP,
							params: { scrollTo: key },
						});
					},
				},
			],
			height: 12,
			isCentered: true,
		});
	};

	public buyInGamePackage = async (offer: Offer): Promise<void> => {
		this.emitGenericClick();

		if (
			offer.timeLimit ||
			(offer.type === OfferType.ACTION_FOR_GEMS && !offer.condition?.())
		)
			return;

		this.ticker.stop();
		const status = await this.checkResources(offer);

		if (status === OfferStatus.HAS_ENOUGH_RESOURCES) {
			// Gem confirmation logic
			if (
				offer.price &&
				"gems" in offer.price &&
				this.settingsStore.showConfirmGemsMessagesShop
			) {
				this.popupStore.showPopup({
					id: PopupDictionary.GEM_CONFIRMATION,
					type: PopupType.INFO,
					heading: translate("ui.shop"),
					body: {
						ctaText: translate("ui.pack_confirm_message"),
						value: offer.value as Record<string, number>,
						price: offer.price as Record<string, number>,
					},
					skin: SkinTypes.PREMIUM,
					showSkipMessageNotice: true,
					actions: [
						{
							label: translate("button.buy"),
							buttonType: StatusTypes.PRIMARY,
							callback: async (): Promise<void> => {
								await this.finalizeInGamePurchase(offer);
								this.popupStore.hidePopup();
							},
						},
						{
							label: translate("button.cancel"),
							buttonType: StatusTypes.DEFAULT,
							callback: async (): Promise<void> => {
								this.resumeGame();
								this.popupStore.hidePopup();
							},
						},
					],
					closeCallback: async (): Promise<void> => this.resumeGame(),
					maxHeight: 20,
					isCentered: true,
					isPaddingless: true,
				});
			} else {
				// Immediate purchase for non-gem or confirmed gem offers
				await this.finalizeInGamePurchase(offer);
			}
		} else if (status === OfferStatus.NOT_ENOUGH_RESOURCES) {
			this.handleMissingResource(Object.keys(offer.price)[0]);
		}
	};

	public openPopup = (
		storeId: string,
		analytics?: OfferAnalytics,
		priority?: boolean,
	): void => {
		const offer = this.shopStore.getItemByStoreId(storeId);
		if (!offer) return;

		this.popupStore.showPopup({
			id: `offer-detail-${offer.store_id}`,
			isPaddingless: true,
			skin: SkinTypes.DARK,
			width: 24.5,
			height: "auto",
			hasOverlap: true,
			hasOuterBar: true,
			component: markRaw(OfferDetailPopup),
			componentProps: { contentData: offer },
			analytics,
			isPriority: priority,
		});
	};

	public closePopup = (): void => {
		this.popupStore.hidePopup();
	};

	public resumeGame = (): void => {
		if (!this.ticker.isLocked) {
			this.ticker.start();
		}
	};

	public emitGenericClick = (): void => {
		SoundService.getInstance().playGenerickClick();
	};
}

// Internal singleton instance
export const PaymentProvider = new PaymentProviderClass();
